import React, { useEffect, useMemo, useRef, useState } from 'react';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';
import useLandingScroll, { demoStateAt, createDemoStore } from '../components/landing/useLandingScroll';
import { IntroPortal, LandingNav, HeroSection, StatementSection, WordsSection } from '../components/landing/LandingHero';
import { DemoPortalSection, GuestsSection, Marquee, DemoStarterSection } from '../components/landing/LandingDemos';
import { HostsSection, CtaSection, FaqSection, LandingFooter } from '../components/landing/LandingTail';
import { DEMO_PHOTO } from '../components/landing/PhoneScreens';
import { RING } from '../components/landing/landingTokens';
import { CAP, WORD_W } from '../utils/portalWordmark';
import '../components/landing/landing.css';

/**
 * Public marketing landing page — "Landing page v3" (design_handoff_portal_landing_rail_pro):
 * dark, scroll-driven. A portal intro, then pinned sections that animate as you scroll, a phone
 * demo that runs the real Portal composer from photo to send to rail, the guest and host
 * stories, a Party Starter going green lit, the stacked host cards, the CTA, the FAQ.
 *
 * Layout lives in React; motion lives in the scroll engine (components/landing/useLandingScroll),
 * which writes frames straight to the DOM. Mobile (390×844) is the priority view: under 900px
 * every pin is one column with the phone first, and the phone zooms so it and its caption fit
 * in 100vh.
 */
const readViewport = () => ({ vw: typeof window === 'undefined' ? 1440 : window.innerWidth, vh: typeof window === 'undefined' ? 900 : window.innerHeight });

export default function LandingPage() {
  const rootRef = useRef(null);
  const reduced = usePrefersReducedMotion();
  const [{ vw, vh }, setViewport] = useState(readViewport);
  // Not page state: the composer alone subscribes (see createDemoStore).
  const demoStore = useMemo(() => createDemoStore(demoStateAt(0, DEMO_PHOTO)), []);

  useEffect(() => {
    const onResize = () => setViewport(readViewport());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useLandingScroll(rootRef, { reduced, onDemo: demoStore.set, photo: DEMO_PHOTO });

  // The handoff's responsive numbers: `narrow` is the single-column phone layout, `short`
  // trims the pinned sections' type so a phone and its caption share one screen.
  const L = useMemo(() => {
    const narrow = vw < 900;
    const short = vh < 820;
    return {
      narrow, short, vw, vh,
      stepFs: narrow ? 13 : short ? 13 : 15,
      stepH3: short ? '20px' : 'clamp(22px,2.4vw,30px)',
      pinAlign: short || narrow ? 'start' : 'center',
      pinPad: narrow ? '68px 18px 14px' : (short ? '88px clamp(24px,5vw,64px) 24px' : '0 clamp(24px,5vw,64px)'),
      pinCols: narrow ? 'minmax(0,1fr)' : 'minmax(0,1fr) auto',
      pinRows: narrow ? 'auto minmax(0,1fr)' : 'auto',
      textOrder: narrow ? 2 : 1,
      phoneOrder: narrow ? 1 : 2,
      stepGap: narrow ? 12 : 18,
      cardCols: narrow ? 'minmax(0,1fr)' : 'minmax(0,1fr) auto',
      cardPad: narrow ? '28px 22px' : 'clamp(28px,4vw,56px)',
      cardPhone: !narrow,
      cardMinH: narrow ? 0 : '82vh',
      cardFs: narrow ? 14 : 16,
      cardH3: narrow ? 'clamp(20px,6vw,24px)' : 'clamp(24px,3vw,34px)',
    };
  }, [vw, vh]);

  // The lockup's cap from the handoff's widths: hero min(560px, 78vw, 70vh), footer min(420px, 72vw).
  const capFor = (width) => Math.max(24, Math.round((width * CAP) / WORD_W));
  const heroCap = capFor(Math.min(560, vw * 0.78, vh * 0.7));
  const footerCap = capFor(Math.min(420, vw * 0.72));

  const stepThrough = (e) => {
    e.preventDefault();
    const el = rootRef.current?.querySelector('[data-sx="portal"]');
    if (!el) return;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY + window.innerHeight * 0.55, behavior: reduced ? 'auto' : 'smooth' });
  };

  return (
    <div ref={rootRef} className="pr-landing pr-land" data-testid="landing">
      <div data-progress="1" aria-hidden style={{ position: 'fixed', left: 0, right: 0, top: 0, height: 2, zIndex: 60, background: RING, transform: 'scaleX(0)', transformOrigin: '0 50%', pointerEvents: 'none' }} />
      {!reduced && <IntroPortal />}
      <LandingNav />
      <HeroSection heroCap={heroCap} onStepThrough={stepThrough} />
      <StatementSection still={reduced} />
      <WordsSection />
      <DemoPortalSection L={L} demoStore={demoStore} />
      <GuestsSection L={L} />
      <Marquee />
      <DemoStarterSection L={L} />
      <HostsSection L={L} />
      <CtaSection />
      <FaqSection />
      <LandingFooter footerCap={footerCap} />
    </div>
  );
}
